/**
 * Property capture — resolves dashboard-configured rules into event
 * properties. The SDK fetches the rule set on init (cached in localStorage
 * for 5 min) and runs the resolver before every event. The special property
 * name `userId` triggers identify() instead of being attached as a property.
 *
 * Sources supported (Phase 1):
 *   localStorage / sessionStorage / cookie  (raw | json | jwt)
 *   window                                  (dot-notation path)
 *   dom                                     (CSS selector + optional attr)
 */

import type { LocalStore } from "./storage.js";
import type { AnyProperties } from "./types.js";

export type SourceType = "localStorage" | "sessionStorage" | "cookie" | "window" | "dom" | "network";
export type SourceFormat = "raw" | "json" | "jwt";
export type Transform = "lowercase" | "uppercase" | "trim" | "number" | "boolean";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "ANY";

export interface CaptureSource {
  type: SourceType;
  key?: string;
  path?: string;
  format?: SourceFormat;
  selector?: string;
  attribute?: string;
  urlMatch?: string;
  method?: HttpMethod;
}

export interface CaptureRule {
  property: string;
  source: CaptureSource;
  transform: Transform | null;
}

export interface RemoteConfig {
  rev: number;
  rules: CaptureRule[];
}

const CACHE_KEY = "config";
const CACHE_TTL_MS = 5 * 60 * 1000;
const NETCACHE_KEY = "netcache";
const NETCACHE_TTL_MS = 60 * 60 * 1000;

const PII_DENYLIST = [
  /password/i, /\bpwd/i, /secret/i, /\btoken\b/i, /api[_-]?key/i,
  /credit/i, /\bcard\b/i, /\bcvv\b/i, /\bcvc\b/i,
  /\bssn\b/i, /private[_-]?key/i,
];

interface OnIdentifyHook {
  (userId: string, traits?: AnyProperties): void;
}

export class CaptureResolver {
  private storage: LocalStore;
  private apiUrl: string;
  private projectId: string;
  private publicKey: string;
  private rules: CaptureRule[] = [];
  private onIdentify: OnIdentifyHook;
  private lastIdentifiedId: string | null = null;
  private debug: boolean;

  // Network capture: values harvested from intercepted fetch responses,
  // keyed by rule.property. Persists for the lifetime of the page.
  private networkCache = new Map<string, unknown>();
  private networkPatched = false;

  constructor(opts: {
    storage: LocalStore;
    apiUrl: string;
    projectId: string;
    publicKey: string;
    onIdentify: OnIdentifyHook;
    debug?: boolean;
  }) {
    this.storage = opts.storage;
    this.apiUrl = opts.apiUrl;
    this.projectId = opts.projectId;
    this.publicKey = opts.publicKey;
    this.onIdentify = opts.onIdentify;
    this.debug = !!opts.debug;
  }

  /** Load rules from cache (synchronous) and kick off a fresh fetch (async). */
  init(): void {
    const cached = this.readCache();
    if (cached) {
      this.rules = cached.rules;
      this.maybeInstallNetworkInterceptor();
    }
    // Restore network-captured values from the previous page load so the
    // very first event after reload (often the $page_view fired in init())
    // still carries them — without waiting for the customer's app to refetch.
    this.restoreNetworkCache();
    void this.refresh();
  }

  private restoreNetworkCache(): void {
    const wrapped = this.storage.getJson<{ at: number; values: Record<string, unknown> }>(NETCACHE_KEY);
    if (!wrapped) return;
    if (Date.now() - wrapped.at > NETCACHE_TTL_MS) {
      this.storage.remove(NETCACHE_KEY);
      return;
    }
    for (const [k, v] of Object.entries(wrapped.values)) {
      if (v != null) this.networkCache.set(k, v);
    }
  }

  private persistNetworkCache(): void {
    if (this.networkCache.size === 0) return;
    const values: Record<string, unknown> = {};
    for (const [k, v] of this.networkCache) values[k] = v;
    this.storage.setJson(NETCACHE_KEY, { at: Date.now(), values });
  }

  /** Force a refresh from server. Awaited callers can know when rules are live. */
  async refresh(): Promise<void> {
    try {
      const url = `${this.apiUrl}/v1/projects/${encodeURIComponent(this.projectId)}/config?publicKey=${encodeURIComponent(this.publicKey)}`;
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) {
        if (this.debug) console.warn(`[analytics] config fetch failed: ${res.status}`);
        return;
      }
      const cfg = (await res.json()) as RemoteConfig;
      this.rules = Array.isArray(cfg.rules) ? cfg.rules : [];
      this.writeCache(cfg);
      this.maybeInstallNetworkInterceptor();
    } catch (e) {
      if (this.debug) console.warn("[analytics] config fetch error", e);
    }
  }

  /**
   * Install a fetch() monkey-patch the first time we see any network rule.
   * Subsequent rule refreshes just update the rule list — patching is a
   * one-shot operation we can't safely undo (other libraries may have
   * stacked their own wrappers on top of ours).
   */
  private maybeInstallNetworkInterceptor(): void {
    if (this.networkPatched) return;
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;
    const hasNetworkRule = this.rules.some((r) => r.source.type === "network");
    if (!hasNetworkRule) return;

    const originalFetch = window.fetch.bind(window);
    const self = this;
    window.fetch = function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const promise = originalFetch(input, init);
      promise.then((res) => {
        try {
          // Don't read our own config endpoint or any non-OK response.
          if (!res.ok) return;
          const url = typeof input === "string"
            ? input
            : (input instanceof URL ? input.toString() : input.url);
          const method = (init?.method ?? (input instanceof Request ? input.method : "GET"))
            .toUpperCase();
          const matching = self.rules.filter(
            (r) => r.source.type === "network"
              && r.source.urlMatch
              && url.toLowerCase().includes(r.source.urlMatch.toLowerCase())
              && (!r.source.method || r.source.method === "ANY" || r.source.method === method),
          );
          if (matching.length === 0) return;
          const ct = res.headers.get("content-type") || "";
          if (!ct.includes("json")) return;
          // Clone so the customer's downstream consumer still gets the body.
          res.clone().json().then((body: unknown) => {
            let changed = false;
            for (const rule of matching) {
              if (!rule.source.path) continue;
              const value = walkPath(body, rule.source.path);
              if (value != null) {
                self.networkCache.set(rule.property, value);
                changed = true;
              }
            }
            if (changed) self.persistNetworkCache();
          }).catch(() => { /* response wasn't JSON despite the header */ });
        } catch {
          /* never break the customer's fetch */
        }
      }).catch(() => { /* customer's fetch rejected — not our concern */ });
      return promise;
    };
    this.networkPatched = true;
  }

  /**
   * Resolve all rules and return any non-userId properties to merge into the
   * event. userId rules trigger identify() as a side effect — they don't
   * land in event.properties (the SDK already attaches event.userId).
   */
  resolveSuper(): AnyProperties {
    const out: AnyProperties = {};
    if (this.rules.length === 0) return out;

    const identifyTraits: AnyProperties = {};
    let identifyId: string | null = null;

    for (const rule of this.rules) {
      // Defensive PII check (server should have rejected, but in case of a
      // tampered cache or older SDK).
      if (PII_DENYLIST.some((p) => p.test(rule.property))) continue;

      const value = rule.source.type === "network"
        ? this.networkCache.get(rule.property) ?? null
        : this.extract(rule.source);
      if (value == null) continue;
      const final = this.applyTransform(value, rule.transform);
      if (final == null || final === "") continue;

      if (rule.property === "userId") {
        identifyId = String(final);
      } else if (rule.property === "email" || rule.property === "fullname" || rule.property === "name") {
        // Common trait names — pass to identify() AND also attach to events
        identifyTraits[rule.property] = final;
        out[rule.property] = final;
      } else {
        out[rule.property] = final;
      }
    }

    if (identifyId && identifyId !== this.lastIdentifiedId) {
      this.lastIdentifiedId = identifyId;
      try {
        this.onIdentify(identifyId, Object.keys(identifyTraits).length > 0 ? identifyTraits : undefined);
      } catch (e) {
        if (this.debug) console.warn("[analytics] onIdentify hook threw", e);
      }
    }

    return out;
  }

  /* ─── extraction ─── */

  private extract(s: CaptureSource): unknown {
    try {
      switch (s.type) {
        case "localStorage":
          if (typeof window === "undefined" || !s.key) return null;
          return this.decodeStored(window.localStorage.getItem(s.key), s);
        case "sessionStorage":
          if (typeof window === "undefined" || !s.key) return null;
          return this.decodeStored(window.sessionStorage.getItem(s.key), s);
        case "cookie":
          if (typeof document === "undefined" || !s.key) return null;
          return this.decodeStored(readCookie(s.key), s);
        case "window": {
          if (typeof window === "undefined" || !s.path) return null;
          return walkPath(window as unknown as Record<string, unknown>, s.path);
        }
        case "dom": {
          if (typeof document === "undefined" || !s.selector) return null;
          const el = document.querySelector(s.selector);
          if (!el) return null;
          if (s.attribute) return el.getAttribute(s.attribute);
          return el.textContent?.trim() ?? null;
        }
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  private decodeStored(raw: string | null, s: CaptureSource): unknown {
    if (raw == null) return null;
    const format = s.format ?? "raw";
    if (format === "raw") return raw;
    if (format === "json") {
      try {
        const parsed = JSON.parse(raw);
        return s.path ? walkPath(parsed as Record<string, unknown>, s.path) : parsed;
      } catch { return null; }
    }
    if (format === "jwt") {
      try {
        const parts = raw.split(".");
        if (parts.length < 2) return null;
        const payload = JSON.parse(b64urlDecode(parts[1]!)) as Record<string, unknown>;
        return s.path ? walkPath(payload, s.path) : payload;
      } catch { return null; }
    }
    return null;
  }

  private applyTransform(value: unknown, transform: Transform | null): unknown {
    if (value == null) return null;
    if (!transform) return typeof value === "string" ? value : String(value);
    switch (transform) {
      case "lowercase": return String(value).toLowerCase();
      case "uppercase": return String(value).toUpperCase();
      case "trim":      return String(value).trim();
      case "number":    { const n = Number(value); return Number.isFinite(n) ? n : null; }
      case "boolean":   {
        const s = String(value).toLowerCase();
        if (s === "true" || s === "1" || s === "yes") return true;
        if (s === "false" || s === "0" || s === "no") return false;
        return null;
      }
      default: return String(value);
    }
  }

  /* ─── cache ─── */

  private readCache(): RemoteConfig | null {
    const wrapped = this.storage.getJson<{ at: number; cfg: RemoteConfig }>(CACHE_KEY);
    if (!wrapped) return null;
    if (Date.now() - wrapped.at > CACHE_TTL_MS) return null;
    return wrapped.cfg;
  }

  private writeCache(cfg: RemoteConfig): void {
    this.storage.setJson(CACHE_KEY, { at: Date.now(), cfg });
  }
}

function walkPath(obj: Record<string, unknown> | unknown, path: string): unknown {
  if (obj == null) return null;
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur ?? null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = encodeURIComponent(name) + "=";
  for (const part of document.cookie.split(";")) {
    const t = part.trim();
    if (t.startsWith(target)) return decodeURIComponent(t.slice(target.length));
  }
  return null;
}

function b64urlDecode(s: string): string {
  // Restore standard base64 alphabet and padding
  let std = s.replace(/-/g, "+").replace(/_/g, "/");
  while (std.length % 4) std += "=";
  if (typeof atob === "function") return atob(std);
  // Node fallback (won't run in browser, but keeps types happy in tests)
  return Buffer.from(std, "base64").toString("utf-8");
}
